import { useEffect, useRef, useState, type FocusEvent } from 'react';
import { withConflictConfirm } from '../availability/conflicts';
import { api } from '../api';
import { formatPln } from '../catalog/money';
import { QuickPicker, type PickerOption } from '../ui/QuickPicker';
import { RowMenu } from '../ui/RowMenu';
import { markStyle, useContractorColors } from '../contractors/colors';
import type { QuoteLine } from './types';

/** As `GET /api/pricing/search` sends it; `priceGrosze` only for a role that may see that price. */
type Hit = { type: 'item' | 'service' | 'package'; id: string; name: string; detail: string; priceGrosze?: number | null; categoryId: string | null };
/** A position of a supplier's catalog (`GET /api/quotes/versions/:id/supplier-search`, owner 2026-10-21 00:32). */
type SupplierHit = { priceId: string; supplierId: string; supplierName: string; itemId: string | null; name: string; priceDayGrosze: number; /** Absent when the role hides it (rule 3). */ quantity?: number | null };
/** `create`: "+ Nowa pozycja w katalogu" (P8, note 22:50) — nothing in the catalog fits the typed name. */
type Option = PickerOption & { hit: Hit | null; text: string; create?: boolean; supplier?: SupplierHit };

/**
 * The small gear left of the new-line field (owner 2026-10-21 00:32): "Wybierz z katalogu" and, for whoever works with
 * suppliers, the switch "Korzystaj z katalogów dostawców" (search also in the active suppliers' catalogs).
 */
/** Owner 20:00: "Wstaw blok…" here too (from the bar over the table), for whoever may insert blocks. */
export type AddGear = { canSuppliers: boolean; useSuppliers: boolean; onToggleSuppliers: () => void; onPickCatalog: () => void; onInsertBlock?: (() => void) | undefined };
/** The line just put in place of another (`POST /api/quotes/lines/:id/replace`). */
export type ReplacedLine = QuoteLine & { replacedId: string };
/**
 * Owner 2026-10-24 16:15 („Jak klikam w wybrana juz pozycje … tak samo jakby to byla pusta pozycja”): the field replaces
 * the line `lineId` (seen at `version`) instead of adding one; the pick keeps its place, quantity, days, discount, notes.
 */
export type ReplaceTarget = { lineId: string; version: number; lineName: string; onReplaced: (line: ReplacedLine) => void };

/** Owner 2026-10-24 16:19: in the supplier's colour (a light tint, the highlight in its colour; grey without one). */
const supplierOption = (h: SupplierHit, colors: ReadonlyMap<string, string>): Option => ({
  key: `sup-${h.priceId}`,
  name: h.name,
  details: [h.supplierName, `${formatPln(h.priceDayGrosze)} / doba`, ...(h.quantity != null ? [`ma ${h.quantity} szt.`] : [])],
  group: 'U dostawców',
  hit: null,
  text: h.name,
  supplier: h,
  mark: markStyle(colors.get(h.supplierId)),
});

const GROUPS: Record<Hit['type'], string> = { item: 'Sprzęt', package: 'Pakiety', service: 'Usługi' };

function toOption(h: Hit): Option {
  // equipment shows "manufacturer model" as in the quote's "Marka" column; its catalog name goes below
  const name = h.type === 'item' && h.detail ? h.detail : h.name;
  const details = [...(h.type === 'item' && h.detail && h.detail !== h.name ? [h.name] : []), ...(typeof h.priceGrosze === 'number' ? [`${formatPln(h.priceGrosze)} / dzień`] : [])];
  return { key: `${h.type}-${h.id}`, name, details, group: GROUPS[h.type], hit: h, text: name };
}

/**
 * Quick picker of a section ("szybkie wybieranie"): equipment, packages (F26) and services in one list grouped "Sprzęt /
 * Pakiety / Usługi", the best match first (in a section made from a category: that category's gear first). "2 qrp40" adds 2. When nothing fits, the typed text can be added as
 * an own line. The new line comes back to `onAdded`.
 */
export function AddPricedLine({
  versionId,
  sectionId,
  sectionName,
  categoryId = null,
  onAdded,
  onNewItem,
  gear,
  replace,
}: {
  versionId: string;
  sectionId: string;
  sectionName: string;
  categoryId?: string | null;
  onAdded: (line: QuoteLine) => void;
  /** With the right to add to the catalog: "+ Nowa pozycja w katalogu" opens its dialog with the typed name. */
  onNewItem?: ((name: string, quantity: number) => void) | undefined;
  /** In a draft: the gear with "Wybierz z katalogu" and the suppliers' switch. */
  gear?: AddGear | undefined;
  /** Replace this line instead of adding one (the field takes the focus at once). */
  replace?: ReplaceTarget | undefined;
}) {
  const [focused, setFocused] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (replace) input.current?.focus();
    // only when it opens
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // read at search time (the colours are loaded long before anyone types)
  const colors = useRef<ReadonlyMap<string, string>>(new Map());
  colors.current = useContractorColors();
  const useSuppliers = !!gear?.canSuppliers && gear.useSuppliers;
  async function search(q: string, signal: AbortSignal): Promise<Option[]> {
    // P8 (point 22): a section made from a category suggests its gear first (nothing is blocked)
    const [res, suppliers] = await Promise.all([
      api<{ hits: Hit[] }>(`/api/pricing/search?q=${encodeURIComponent(q)}&limit=12${categoryId ? `&categoryId=${categoryId}` : ''}`, { signal }),
      useSuppliers ? api<{ hits: SupplierHit[] }>(`/api/quotes/versions/${versionId}/supplier-search?q=${encodeURIComponent(q)}${categoryId ? `&categoryId=${categoryId}` : ''}`, { signal }).then((r) => r.hits) : Promise.resolve([]),
    ]);
    const own: Option = { key: 'custom', name: q, details: [replace ? 'zmień na pozycję własną' : 'dodaj jako pozycję własną'], group: 'Pozycja własna', hit: null, text: q };
    const hits = res.hits;
    // nothing in the catalog fits: it can be added there at once
    const create: Option[] = onNewItem && !replace && q.trim() !== '' && !hits.some((h) => h.type === 'item') ? [{ key: 'new-item', name: q, details: ['+ Nowa pozycja w katalogu'], group: 'Katalog', hit: null, text: q, create: true }] : [];
    // owner 22:05: my gear is always added as mine — a shortage is rented with „Dopożycz” under its line; only what I do
    // not have (a supplier's own position) comes whole from a supplier. A supplier's position of MY item found only at
    // the supplier (e.g. by the supplier's name or description) brings my item instead (review: never dropped).
    const mine = new Set(hits.filter((h) => h.type === 'item').map((h) => h.id));
    const myItems: Option[] = [];
    for (const h of suppliers) {
      if (h.itemId === null || mine.has(h.itemId)) continue;
      mine.add(h.itemId);
      // `name` of a position of MY item is my catalog's name (the server sends the item's, not „Nazwa u dostawcy”)
      myItems.push({ ...toOption({ type: 'item', id: h.itemId, name: h.name, detail: '', categoryId: null }), details: ['z mojego katalogu'] });
    }
    return [...hits.map(toOption), ...myItems, ...suppliers.filter((h) => h.itemId === null).map((h) => supplierOption(h, colors.current)), own, ...create];
  }

  /** The pick in place of `replace`'s line (its quantity stays: a typed "2 qrp40" changes only the item). */
  async function replaceBy(r: ReplaceTarget, option: Option) {
    const h = option.hit;
    const body = option.supplier ? { priceId: option.supplier.priceId } : h === null ? { type: 'custom', name: option.text } : h.type === 'item' ? { type: 'item', itemId: h.id } : h.type === 'package' ? { type: 'package', packageId: h.id } : { type: 'service', serviceId: h.id };
    // gear another event reserved first: „Dodaj mimo to” / „Anuluj” (#171); „Anuluj” keeps the typed text
    const line = await withConflictConfirm((confirmConflicts) => api<ReplacedLine>(`/api/quotes/lines/${r.lineId}/replace`, { method: 'POST', body: { ...body, version: r.version, ...(confirmConflicts ? { confirmConflicts } : {}) } }));
    if (!line) return false;
    r.onReplaced(line);
    return true;
  }

  async function add(option: Option, quantity: number) {
    if (replace) return replaceBy(replace, option);
    if (option.create) return onNewItem?.(option.text, quantity);
    // owner 2026-10-21: from a supplier's catalog — the line and its rental at that supplier
    if (option.supplier) {
      onAdded(await api<QuoteLine>(`/api/quotes/versions/${versionId}/supplier-lines`, { method: 'POST', body: { sectionId, priceId: option.supplier.priceId, quantity } }));
      return;
    }
    const h = option.hit;
    const body = h === null ? { type: 'custom', name: option.text } : h.type === 'item' ? { type: 'item', itemId: h.id } : h.type === 'package' ? { type: 'package', packageId: h.id } : { type: 'service', serviceId: h.id };
    // gear another event reserved first: „Dodaj mimo to” / „Anuluj” (owner 2026-10-24); „Anuluj” keeps the typed text
    const line = await withConflictConfirm((confirmConflicts) => api<QuoteLine>(`/api/quotes/versions/${versionId}/lines`, { method: 'POST', body: { sectionId, quantity, ...body, ...(confirmConflicts ? { confirmConflicts } : {}) } }));
    if (!line) return false;
    onAdded(line);
    return true;
  }

  // owner 18:41: the field stays shown while it holds text (quotes.css `.has-text`)
  const [hasText, setHasText] = useState(false);
  const readText = () => setHasText((box.current?.querySelector('input')?.value ?? '') !== '');
  // the gear shows while the field (or the gear's own menu) has the focus
  const onBlur = (e: FocusEvent<HTMLDivElement>) => {
    if (!(e.relatedTarget instanceof Node && box.current?.contains(e.relatedTarget))) setFocused(false);
    readText();
  };
  const picker = (
    <QuickPicker<Option>
      label={replace ? `Zmień pozycję „${replace.lineName}” na inną` : `Dodaj pozycję do sekcji „${sectionName}”`}
      placeholder={replace ? (useSuppliers ? 'Zmień na: też z katalogów dostawców' : 'Zmień na: wpisz nazwę') : useSuppliers ? 'Dodaj: też z katalogów dostawców' : 'Dodaj: np. 2 qrp40, realizacja'}
      search={search}
      onPick={add}
      inputRef={input}
      featureId="quotes.lines"
      labelHidden
      floating
    />
  );
  const actions = gear
    ? [
        // replacing a line (owner 2026-10-24 16:15) keeps only the suppliers' switch: the catalog's page and blocks add lines
        ...(replace ? [] : [{ label: 'Wybierz z katalogu', onSelect: gear.onPickCatalog }]),
        ...(gear.canSuppliers ? [{ label: 'Korzystaj z katalogów dostawców', checked: gear.useSuppliers, onSelect: gear.onToggleSuppliers }] : []),
        ...(gear.onInsertBlock && !replace ? [{ label: 'Wstaw blok…', onSelect: gear.onInsertBlock }] : []),
      ]
    : [];
  if (!gear || actions.length === 0) return replace ? <div className="q-add q-replace">{picker}</div> : picker;
  return (
    <div className={`q-add${replace ? ' q-replace' : ''}${focused ? ' focused' : ''}${hasText ? ' has-text' : ''}${useSuppliers ? ' q-add-suppliers' : ''}`} ref={box} onFocus={() => setFocused(true)} onBlur={onBlur} onInput={readText}>
      {/* owner 2026-10-23 19:29 („nie pokazuj tych pol od razu … dopiero jak najade na ten pusty wiersz. plusik tez usun”):
          ≥ 900 px the field and its ⚙ show only while the pointer is over the add row itself, the focus is in them, the
          field has text or the ⚙ menu is open (quotes.css); a click anywhere in the row focuses the field (SheetAddRow). */}
      {/* owner 2026-10-23 16:07: always in place, in the gutter left of the field; quotes.css shows it on hover / focus
          (≥ 900 px) and, on a phone, only while ".focused" as before. The input keeps the focus when the gear is
          pressed (Safari does not focus buttons on click). */}
      <span className="q-add-gear" onMouseDown={(e) => e.preventDefault()} data-feature-id="quotes.suppliers">
        <RowMenu
          icon="gear"
          label="Więcej sposobów dodawania"
          floating
          actions={actions}
        />
      </span>
      {picker}
    </div>
  );
}
